/**
 * @file apiFiles.js
 * @description Módulo de API para el MVP "Mis archivos".
 * @author SIVOL Technologies
 * @version 1.0.0
 */

import { API_ENDPOINTS } from '../utils/constants';
import { request as rawApiRequest } from '../services/apiClient';

/**
 * @param {string|null|undefined} cd - Valor bruto de Content-Disposition
 * @returns {string|null} Nombre de archivo sugerido o null
 */
export function parseFilenameFromContentDisposition(cd) {
  if (!cd || typeof cd !== 'string') return null;
  const star = /filename\*=(?:UTF-8''|utf-8'')([^;\n]+)/i.exec(cd);
  if (star && star[1]) {
    try {
      const raw = star[1].trim().replace(/^["']|["']$/g, '');
      return decodeURIComponent(raw);
    } catch (_) {
      /* ignore */
    }
  }
  const fn = /filename\s*=\s*("?)([^";\n]+)\1/i.exec(cd);
  if (fn && fn[2]) {
    let name = fn[2].trim();
    if (name.startsWith('"') && name.endsWith('"')) name = name.slice(1, -1);
    try {
      return decodeURIComponent(name);
    } catch (_) {
      return name;
    }
  }
  return null;
}

/**
 * @param {string|undefined} name
 * @returns {string|null} Solo el nombre final (sin path)
 */
function safeClientBasename(name) {
  if (typeof name !== 'string' || !name.trim()) return null;
  const base = name.replace(/^\s+/, '').replace(/[/\\]/g, '/').split('/').pop();
  if (!base || !base.trim()) return null;
  return base.trim().slice(0, 500);
}

/**
 * GET binario /api/files.php?action=download con Authorization (evita descarga vía ancla sin cabecera Bearer).
 * @param {number|string} messageId
 * @returns {Promise<
 *   | { ok: true, blob: Blob, contentType: string, headerFilename: string|null }
 *   | { ok: false, status: number, error: string }
 * >}
 */
async function fetchFileBinaryFromMessagesApi(messageId) {
  const id = String(messageId);
  const url = `${API_ENDPOINTS.FILES}?action=download&id=${encodeURIComponent(id)}`;
  const res = await rawApiRequest(url, { method: 'GET' });
  const status = res.status;
  const rawCt = (res.headers.get('Content-Type') || '').split(';')[0].trim().toLowerCase();
  if (!res.ok) {
    let err = `http_${status}`;
    try {
      if (rawCt.includes('json') || status === 400 || status === 401 || status === 403 || status === 404) {
        const j = await res.json();
        err = j && (j.error || j.message) ? String(j.error || j.message) : err;
      }
    } catch (_) {
      /* cuerpo no JSON */
    }
    return { ok: false, status, error: err };
  }
  if (rawCt.includes('application/json')) {
    try {
      const j = await res.json();
      return { ok: false, status, error: String(j?.error || 'invalid_response') };
    } catch (_) {
      return { ok: false, status, error: 'invalid_response' };
    }
  }
  const headerFilename = parseFilenameFromContentDisposition(res.headers.get('Content-Disposition'));
  const blob = await res.blob();
  const contentType =
    rawCt && !rawCt.includes('text/html') ? rawCt : (blob.type || 'application/octet-stream');
  return { ok: true, blob, contentType, headerFilename };
}

/**
 * @description Descarga el binario del archivo con Authorization
 *   y X-Tenant-ID, sin parsear JSON. Para vista previa en modal (imagen / PDF vía blob URL).
 * @param {number|string} messageId - ID del mensaje en `mensajes`.
 * @returns {Promise<{ok:true, blob:Blob, contentType:string}|{ok:false, status:number, error?:string}>}
 */
export async function fetchFileBlobForPreview(messageId) {
  const r = await fetchFileBinaryFromMessagesApi(messageId);
  if (!r.ok) return r;
  return { ok: true, blob: r.blob, contentType: r.contentType };
}

/**
 * Fuerza descarga en el navegador; revoca el object URL tras un breve delay.
 * @param {Blob} blob
 * @param {string} filename
 */
export function triggerBlobFileDownload(blob, filename) {
  const name = safeClientBasename(filename) || 'archivo';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => {
    try {
      URL.revokeObjectURL(url);
    } catch (_) {
      /* */
    }
  }, 2500);
}

/**
 * Descarga con Bearer; no abre solo el href del ancla (no envía Authorization → 401 JSON y nombre erróneo).
 * @param {number|string} messageId
 * @param {string} [suggestedOriginalName] - p. ej. `archivo_nombre_original` del listado o mensaje
 * @returns {Promise<{ok:true, filename:string}|{ok:false, status:number, error:string}>}
 */
export async function downloadMessageAttachment(messageId, suggestedOriginalName) {
  const r = await fetchFileBinaryFromMessagesApi(messageId);
  if (!r.ok) return r;
  const fromHeader = r.headerFilename;
  const fromList = safeClientBasename(suggestedOriginalName);
  const filename = fromHeader || fromList || 'archivo';
  triggerBlobFileDownload(r.blob, filename);
  return { ok: true, filename };
}

export const apiFilesMethods = {
  /**
   * @description Lista archivos enviados/recibidos del usuario autenticado.
   * @param {Object} [opts] - Opciones de filtro/paginación.
   * @param {string} [opts.filter='all'] - all|sent|received|images|documents
   * @param {number} [opts.limit=50] - Máximo de resultados.
   * @param {number} [opts.offset=0] - Offset para paginación.
   * @returns {Promise<{success:boolean, files:Array, count:number}>}
   */
  async getFiles({ filter = 'all', limit = 50, offset = 0 } = {}) {
    const qs = new URLSearchParams({ action: 'list', filter, limit, offset });
    return this.request(`${API_ENDPOINTS.FILES}?${qs}`, { _skipGlobalErrorModal: true });
  },

  /**
   * @description Genera la URL de descarga de un archivo por su ID de mensaje.
   * @param {number} messageId - ID del mensaje con archivo.
   * @returns {string} URL relativa de descarga.
   */
  getFileDownloadUrl(messageId) {
    return `${API_ENDPOINTS.FILES}?action=download&id=${messageId}`;
  },

  /**
   * @description Oculta un archivo para el usuario (soft delete).
   * @param {number} messageId - ID del mensaje.
   * @returns {Promise<{success:boolean}>}
   */
  async deleteFileForMe(messageId) {
    return this.request(`${API_ENDPOINTS.FILES}?action=delete_for_me`, {
      method: 'POST',
      body: JSON.stringify({ message_id: messageId }),
      headers: { 'Content-Type': 'application/json' }
    });
  }
};
